import { Injectable } from '@angular/core';

import { ElementRef, Renderer2 } from '@angular/core';
import { SharedOperationService } from './shared-operation.service';
import { FormBuilderUDFService } from './form-builder-udf-service';
import { ListOperationService } from './list-operation.service';


@Injectable({
  providedIn: 'root'
})
export class DropdownOperationService {
  jsonData: any;
  dataFromShared: any;
  agGridInstances: any[] = [];
  renderer: Renderer2;
  container: any;

  constructor(private formService: FormBuilderUDFService, private sharedService: SharedOperationService,private listOperationService: ListOperationService ) {
    this.sharedService.formViewerData$.subscribe((data) => {
      if (data) {
        this.dataFromShared = data;
      }
    });
  }

  private dropdownUpdateState: { [key: string]: boolean } = {};

  // Stores full option list per dropdown name so the search filter can restore them
  private dropdownOptions: Map<string, { text: string; value: string }[]> = new Map();

  private populateSelectOptions(
    dropdown: HTMLSelectElement,
    list: any[],
    parsedField: { text: string; value: string },
    renderer: Renderer2
  ): void {
    dropdown.innerHTML = '';
    const options: { text: string; value: string }[] = [];
    list.forEach((item: any) => {
      const option = renderer.createElement('option');
      option.text = item[parsedField.text];
      option.value = item[parsedField.value];
      renderer.appendChild(dropdown, option);
      options.push({ text: String(item[parsedField.text] ?? ''), value: String(item[parsedField.value] ?? '') });
    });
    this.dropdownOptions.set(dropdown.name, options);
  }

  private attachSearchInput(dropdown: HTMLSelectElement, renderer: Renderer2): void {
    // Already wrapped — skip
    if (dropdown.parentElement?.classList.contains('custom-select-wrapper')) return;

    const dropdownName = dropdown.name;

    // ── Wrapper ────────────────────────────────────────────────────────────
    const wrapper = document.createElement('div');
    wrapper.className = 'custom-select-wrapper';

    // ── Trigger (shows selected value) ─────────────────────────────────────
    const trigger = document.createElement('div');
    trigger.className = 'custom-select-trigger';
    trigger.setAttribute('tabindex', '0');
    trigger.textContent = dropdown.options[dropdown.selectedIndex]?.text || 'Select...';

    // ── Panel (hidden by default) ──────────────────────────────────────────
    const panel = document.createElement('div');
    panel.className = 'custom-select-panel';
    panel.style.display = 'none';

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.placeholder = 'Search...';
    searchInput.className = 'dropdown-filter-input';

    const optionsList = document.createElement('div');
    optionsList.className = 'custom-select-options';

    panel.appendChild(searchInput);
    panel.appendChild(optionsList);
    wrapper.appendChild(trigger);
    wrapper.appendChild(panel);

    // Insert wrapper before <select>, then move <select> inside wrapper
    dropdown.parentNode!.insertBefore(wrapper, dropdown);
    wrapper.appendChild(dropdown);
    dropdown.style.display = 'none';

    // ── Build option items from the stored full list ────────────────────────
    const buildOptionItems = (filter = '') => {
      optionsList.innerHTML = '';
      const all = this.dropdownOptions.get(dropdownName) ?? [];
      const filtered = filter ? all.filter(o => o.text.toLowerCase().includes(filter.toLowerCase())) : all;
      filtered.forEach(o => {
        const item = document.createElement('div');
        item.className = 'custom-select-option';
        item.textContent = o.text;
        item.setAttribute('data-value', o.value);
        item.addEventListener('mousedown', (e) => {
          e.preventDefault();
          dropdown.value = o.value;
          trigger.textContent = o.text;
          panel.style.display = 'none';
          searchInput.value = '';
          buildOptionItems();
          dropdown.dispatchEvent(new Event('change', { bubbles: true }));
        });
        optionsList.appendChild(item);
      });
    };

    buildOptionItems();
    (dropdown as any)._buildOptionItems = buildOptionItems;
    (dropdown as any)._customTrigger   = trigger;

    // ── Open / close ───────────────────────────────────────────────────────
    trigger.addEventListener('click', () => {
      const isOpen = panel.style.display !== 'none';
      panel.style.display = isOpen ? 'none' : 'block';
      if (!isOpen) { searchInput.focus(); }
    });

    searchInput.addEventListener('blur', () => {
      setTimeout(() => { panel.style.display = 'none'; }, 200);
    });

    // ── Filter ─────────────────────────────────────────────────────────────
    searchInput.addEventListener('input', () => {
      buildOptionItems(searchInput.value.trim());
    });
  }

  /**
   * Syncs the custom-select trigger text with the <select> element's current value.
   * Needed after the value is set programmatically (e.g. patching fields on edit),
   * because the native <select> is hidden behind the custom trigger and won't update on its own.
   */
  syncCustomTrigger(dropdown: HTMLSelectElement): void {
    const trigger = (dropdown as any)._customTrigger as HTMLElement | undefined;
    if (trigger) {
      trigger.textContent = dropdown.options[dropdown.selectedIndex]?.text || 'Select...';
    }
  }

  private resetSearchInput(dropdown: HTMLSelectElement): void {
    const searchInput = dropdown.parentElement?.querySelector('.dropdown-filter-input') as HTMLInputElement | null;
    if (searchInput) searchInput.value = '';
    if ((dropdown as any)._buildOptionItems) (dropdown as any)._buildOptionItems();
    // After repopulation the native <select> auto-selects its first option; reflect that
    // in the custom trigger (instead of forcing 'Select...') so the cascaded value the form
    // actually submits and the next dependent level reads stays visible to the user.
    this.syncCustomTrigger(dropdown);
  }

  async loadAndPopulateComponents(jsonData: any, el: ElementRef, renderer: Renderer2) {
    this.jsonData = jsonData;
    const container = el.nativeElement === undefined ? this.dataFromShared?.nativeElement : el.nativeElement;
    this.renderer = renderer;
    this.container = container;
    for (const component of jsonData.added_components) {
      if (component.addedComponents) {
        for (const innerComponents of component.addedComponents) {
          for (const innerComponent of innerComponents) {
            if (innerComponent.elementName === 'dropdown') {
              await this.populateDropdown(innerComponent.element, container, renderer);
              const firstDropdown = container.querySelector(`select[name="${innerComponent.element.field_name}"]`) as HTMLSelectElement;
              if (firstDropdown) {
                this.onDropdownChange(innerComponent.element.field_name, el, renderer);
              }
            } else if (innerComponent.elementName === 'list') {
              //const listOperationService = new ListOperationService(this.sharedService, this.formService);
              await this.listOperationService.createListGrid(innerComponent, container);
            }
          }
        }
      }
    }
  }

  async populateDropdown(element: any, container: HTMLElement, renderer: Renderer2): Promise<void> {
    const params = this.sharedService.constructDependentParams(element.field_name, container, this.getDropdownConfig.bind(this));
    const dropdownName = element.field_name;
    if (element.datasourceUrl) {
      return new Promise<void>((resolve, reject) => {
        this.sharedService.fetchDataFromAPI(params, element.datasourceUrl, element.requestType, element.apiDirectoryId, element.isExternalAPI).then(data => {
          const dropdown = container.querySelector(`select[name="${dropdownName}"]`) as HTMLSelectElement;

          if (dropdown) {
            //const list = element.resultsetName !== '' ? data[element.resultsetName] : data;
            const list = element.resultsetName !== '' && element.resultsetName in data
              ? data[element.resultsetName]
              : data;

            const parsedField = JSON.parse(element.resultsetfieldName);
            this.populateSelectOptions(dropdown, list ?? [], parsedField, renderer);
            this.attachSearchInput(dropdown, renderer);

            dropdown.addEventListener('change', () => {
              this.onDropdownChange(dropdownName, { nativeElement: container }, renderer);
            });

            resolve();
          } else {
            console.error(`Dropdown with name ${dropdownName} not found or list not found in the response.`);
            reject();
          }
        }).catch(error => {
          console.error(`Error fetching data for ${dropdownName}:`, error);
          reject();
        });
      });
    } else {
      return Promise.resolve();
    }
  }

  async onDropdownChange(dropdownName: string, el: ElementRef, renderer: Renderer2) {
    const dependentDropdownNames = this.getDependentDropdownName(dropdownName);
    if (dependentDropdownNames === undefined) {
      return;
    }
    for (const dependentDropdownName of dependentDropdownNames) {
      const dependentParams = this.sharedService.constructDependentParams(dependentDropdownName, el.nativeElement, this.getDropdownConfig.bind(this), dropdownName);
      if (dependentParams) {
        await this.updateDependentDropdown(dependentDropdownName, dependentParams, el, renderer);
        const dependentDropdown = this.container === undefined ? this.dataFromShared?.nativeElement?.querySelector(`select[name="${dependentDropdownName}"]`) as HTMLSelectElement : this.container?.querySelector(`select[name="${dependentDropdownName}"]`) as HTMLSelectElement;
        if (dependentDropdown && dependentDropdown.options.length > 0) {
          setTimeout(() => {
            this.onDropdownChange(dependentDropdownName, el, renderer);
          }, 0);
        }
      }
    }

    this.dropdownUpdateState[dropdownName] = false;
  }

  async updateDependentDropdown(dependentDropdownName: string, params: any, el: ElementRef, renderer: Renderer2): Promise<void> {
    const container = el.nativeElement === undefined ? this.dataFromShared?.nativeElement : el.nativeElement;
    const dependentDropdown = this.findDropdownElement(dependentDropdownName, container);
    const elementConfig = this.getDropdownConfig(dependentDropdownName);
    if (dependentDropdown && elementConfig.elementName === 'dropdown') {
      if (elementConfig.elementName === 'dropdown') {
        return new Promise<void>((resolve, reject) => {
          this.sharedService.fetchDataFromAPI(params, elementConfig.element.datasourceUrl, elementConfig.element.requestType, elementConfig.element.apiDirectoryId, elementConfig.element.isExternalAPI).then(data => {
           // const list = elementConfig.element.resultsetName !== '' ? data[elementConfig.element.resultsetName] : data;
           const list = elementConfig?.element.resultsetName !== '' && elementConfig?.element.resultsetName in data
           ? data[elementConfig?.element.resultsetName]
           : data;
            if (list) {
              const parsedField = JSON.parse(elementConfig.element.resultsetfieldName);
              // Preserve the current selection (e.g. a value patched in on edit) so the
              // re-fetch triggered by the parent's cascade does not wipe it. Only restore
              // if that value still exists in the refreshed option list.
              const previousValue = dependentDropdown.value;
              this.populateSelectOptions(dependentDropdown, list, parsedField, renderer);
              if (previousValue && Array.from(dependentDropdown.options).some(o => o.value === previousValue)) {
                dependentDropdown.value = previousValue;
              }
              this.attachSearchInput(dependentDropdown, renderer);
              this.resetSearchInput(dependentDropdown);
              resolve();
            } else {
              console.error(`List not found in the response for ${dependentDropdownName}`);
              reject();
            }
          }).catch(error => {
            console.error(`Error fetching data for ${dependentDropdownName}:`, error);
            reject();
          });
        });
      } else {
        return Promise.resolve();
      }
    } else if (elementConfig?.elementName === 'list' && elementConfig?.element?.datasourceUrl) {
      //const listOperationService = new ListOperationService(this.sharedService, this.formService);
      await this.listOperationService.createListGrid(elementConfig, container);
    } else {
      return Promise.resolve();
    }
  }

  findDropdownElement(dropdownName: string, container: HTMLElement): HTMLSelectElement | null {
    return container === undefined ? this.dataFromShared?.nativeElement?.querySelector(`select[name="${dropdownName}"]`) as HTMLSelectElement : container?.querySelector(`select[name="${dropdownName}"]`) as HTMLSelectElement;
  }

  getDropdownConfig(dropdownName: string): any {
    let config = null;
    this.jsonData = this.jsonData === undefined ? this.dataFromShared?.json_data : this.jsonData;
    this.jsonData.added_components.forEach((component: any) => {
      if (component.addedComponents) {
        component.addedComponents.forEach((innerComponents: any[]) => {
          innerComponents.forEach((innerComponent: any) => {
            if (innerComponent.elementName === 'dropdown' && innerComponent.element.field_name === dropdownName) {
              config = innerComponent;
            } else if (innerComponent.elementName === 'list' && innerComponent.element.field_name === dropdownName) {
              config = innerComponent;
            }
          });
        });
      }
    });
    return config;
  }

  getDependentDropdownName(currentDropdownName: string): string[] {
    const elementConfig = this.getDropdownConfig(currentDropdownName);
    return elementConfig ? elementConfig?.element.dependenton?.split(',') : [];
  }

}
